import React, { useEffect, useCallback } from 'react';
import AKModalDialog, {
	type ModalDialogProps,
	type ModalDialogProps as AKModalDialogProps,
	type OnCloseCompleteHandler,
} from '@atlaskit/modal-dialog';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { useModalDialogActions, useReturnFocusTo } from '../../controllers';

type Props = Omit<AKModalDialogProps, 'shouldReturnFocus'>;

const ModalDialogOld = ({ onCloseComplete, ...rest }: Props) => {
	const returnFocusTarget = useReturnFocusTo();
	const { setReturnFocusTo } = useModalDialogActions();

	const handleCloseComplete: OnCloseCompleteHandler = useCallback(
		(element: HTMLElement) => {
			onCloseComplete && onCloseComplete(element);
		},
		[onCloseComplete],
	);

	useEffect(
		() => () => {
			if (returnFocusTarget?.current) {
				returnFocusTarget.current.focus();
				setReturnFocusTo(null);
			}
		},
		[returnFocusTarget, setReturnFocusTo],
	);

	/**
	 * - If there's no returnFocusTarget, let ModalDialog
	 *   handle passing focus back on close when necessary
	 * - If returnFocusTarget is defined, we'll handle
	 *   passing focus back to this element
	 */
	const shouldReturnFocus = !returnFocusTarget;

	return (
		<AKModalDialog
			shouldReturnFocus={shouldReturnFocus}
			onCloseComplete={handleCloseComplete}
			{...rest}
		/>
	);
};

const ModalDialogNew = ({ shouldReturnFocus, ...rest }: ModalDialogProps) => {
	const returnFocusTarget = useReturnFocusTo();
	const { setReturnFocusTo } = useModalDialogActions();

	useEffect(
		() => () => {
			if (returnFocusTarget?.current) {
				!shouldReturnFocus && returnFocusTarget.current.focus();
				setReturnFocusTo(null);
			}
		},
		[returnFocusTarget, setReturnFocusTo, shouldReturnFocus],
	);

	/**
	 *  when shouldReturnFocus is defined,
	 * 	we won't handle passing focus back to this element
	 *	we will ignore the returnFocusTarget
	 */
	const returnFocus = shouldReturnFocus ?? !returnFocusTarget;

	return <AKModalDialog shouldReturnFocus={returnFocus} {...rest} />;
};

export const ModalDialog = componentWithFG(
	'a11y-jira-team_software_modal_type_change',
	ModalDialogNew,
	ModalDialogOld,
);
